import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { GlobalConstants } from '../../../constants/global-constants'
import { Observable, Subscription } from 'rxjs'
import { Store } from '@ngrx/store'
import { AppState } from '../../../store/state/app.state'
import { LOAD_ACTIVITY_SUMMARY_DETAILS } from '../../../store/action/activity.action'
import { ActivityAPIModel, PaginatedAPI, SelectedCompanyAPIModel } from '../../../models/api.model'
import { activitySummaryDetailsSelector } from '../../../store/selector/activity.selector'
import {
  ACTIVITY_ID,
  ACTIVITY_NAME,
  TASK_CURRENT_STATUS,
  DAYS_ELAPSED,
  REQUEST_INITIATED_ON,
  RESPONSE_DUE_BY,
  OWNER_AUTOCOMPLETE_DISPLAY_ID,
  ASSIGNED_TO_DISPLAY_ID,
  LAST_UPDATED_BY,
  LAST_UPDATED_ON,
  TASK_SUB_CATEGORY,
  COMPANY_ENTITY_DISPLAY,
  PREVIOUS_PF
} from '../../../constants/ui.columns'
import { ServiceUrlConstants } from '../../../constants/service-url-constants'
import { DocumentService } from '../../../services/common/document.service'
import {
  getSelectedCompaniesFromState,
  getTaskCategorySubCategoryMapFromState,
  getUIStatusMappingFromState
} from '../../../store/selector/initialize.selector'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { ACTIVITY } from 'src/app/constants/field-id-constants'
import { SummaryTableService } from 'src/app/services/common/summary-table.service'
import { generateOptionsMapFromSet } from '../../../services/common/common.service'

@Component({
  selector: 'app-activity-summary',
  templateUrl: './activity-summary.component.html',
  styleUrls: ['./activity-summary.component.scss']
})
export class ActivitySummaryComponent implements OnInit, OnDestroy {
  fromDate: any
  toDate: any
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<ActivityAPIModel>
  activitySummaryAPIData$: Observable<PaginatedAPI<ActivityAPIModel>>
  private uiStatusMapping: Map<string, string>
  private taskSubCategoryMap: Map<string, string>
  private companyEntityMap: Map<string, string>
  pageTitle = 'Activities Summary'
  dashboardPageLink = `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.DASHBOARD}`
  commonSubscription: Subscription
  selectedViewForOption: any
  viewForOptions: any[]
  isVisible: boolean = true
  readonly MODULE_NAME_ACTIVITY_TRACKER = GlobalConstants.MODULE.ACTIVITY_TRACKER
  getSelectedCompaniesFromState$: Subscription
  companies: SelectedCompanyAPIModel[]

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
    private readonly summaryTableService: SummaryTableService
  ) {}

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    mapReferenceDocuments: false,
    sortBy: 'createdDatetime',
    sortOrder: 'DESC'
  }

  searchRequestBody: any = {
    createdDatetimeRange: undefined
  }

  //TODO: Refactor ngOnInit to smaller functions
  ngOnInit(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe(uiStatusMapping => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState(this.MODULE_NAME_ACTIVITY_TRACKER))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          const subCategoriesArr = Array.from(categorySubCategoryMap?.values()).flatMap(array => array)
          const uniqueSubCategories = new Set(subCategoriesArr)
          this.taskSubCategoryMap = generateOptionsMapFromSet(uniqueSubCategories)
        }
      })
    this.getSelectedCompaniesFromState$ = this.appState
      .select(getSelectedCompaniesFromState)
      .subscribe(selectedCompanies => {
        if (selectedCompanies) {
          const filteredCompanies = selectedCompanies.filter(sc => sc.type === GlobalConstants.COMPANY_TYPE.ENTITY)
          this.companyEntityMap = new Map<string, string>()
          filteredCompanies?.forEach(company => {
            this.companyEntityMap.set(company.id, company.displayCode)
          })
        }
      })

    this.activitySummaryAPIData$ = this.appState.select(activitySummaryDetailsSelector())

    this.summaryOptions = {
      downloadable: true,
      additionalButtons: true,
      contentSelectable: true,
      contentSelector: true,
      filterable: true,
      formattedContent: true,
      pageable: false,
      sortable: true,
      quickFilters: true
    }

    this.viewForOptions = this.summaryTableService.buildViewForOption()
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = { ...this.buildCreatedatetimeFilter(firstViewForOption) }
    this.summaryData = {
      content$: this.activitySummaryAPIData$,
      contentSelector: {
        label: 'View for',
        selectedValue: this.selectedViewForOption,
        selectionOptions: this.viewForOptions,
        onChange: this.onViewForChange
      },
      cssClasses: 'rowFormatting',
      formattingLegend: [
        {
          formatLabel: 'High Priority',
          formatClassName: 'priority-high'
        },
        {
          formatLabel: 'Medium Priority',
          formatClassName: 'priority-medium'
        },
        {
          formatLabel: 'Low Priority',
          formatClassName: 'priority-low'
        }
      ],
      headers: [
        ACTIVITY_ID,
        ACTIVITY_NAME,
        OWNER_AUTOCOMPLETE_DISPLAY_ID,
        REQUEST_INITIATED_ON,
        DAYS_ELAPSED,
        PREVIOUS_PF,
        TASK_CURRENT_STATUS,
        ASSIGNED_TO_DISPLAY_ID,
        LAST_UPDATED_BY,
        LAST_UPDATED_ON
      ],
      additionalButtons: [
        {
          id: 'create-activity',
          label: 'CREATE NEW ACTIVITY',
          route: `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.NEW}`
        }
      ],
      sortCallback: this.sortCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      currentSearchParams: this.queryParamValues,
      searchCallback: this.searchCallback,
      downloadDocumentCallback: this.downloadDocumentCallback,
      checkBoxSelectionIds: [],
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange,
      quickFilters: [
        {
          id: 'newJoineeWF',
          label: 'New Joinee WF',
          criteria: [
            {
              formControlName: 'name',
              filterValue: 'New Joinee Workflow'
            }
          ]
        }
      ]
    }
    this.addFiltersWithDynamicOptions()

    this.appState
      .select('common')
      .subscribe(data => {
        const activitySearchParams = data?.summaryTableData?.get(ACTIVITY)
        if (activitySearchParams) {
          const status = activitySearchParams?.searchRequestBody?.statusList?.[0]
          const parentStatus = this.uiStatusMapping?.[status]
          let searchRequestBody = activitySearchParams?.searchRequestBody
          if (parentStatus) {
            searchRequestBody = { ...searchRequestBody, statusList: parentStatus }
          }
          this.searchRequestBody = {
            ...this.searchRequestBody,
            createdDatetimeRange: activitySearchParams.searchRequestBody?.createdDatetimeRange
          }
          const contentSelector = {
            ...this.summaryData.contentSelector,
            selectedValue: activitySearchParams.selectedViewForOption
          }
          this.summaryData = { ...this.summaryData, appliedFilters: searchRequestBody, contentSelector }

          this.appState.dispatch(LOAD_ACTIVITY_SUMMARY_DETAILS(activitySearchParams))
        } else {
          this.loadSummaryData()
        }
      })
      .unsubscribe()
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(LOAD_ACTIVITY_SUMMARY_DETAILS(options))
    this.appState.dispatch(
      SAVE_SUMMARY_TABLE_SEARCH_PARAMS({
        owner: ACTIVITY,
        data: { ...options, selectedViewForOption: this.selectedViewForOption }
      })
    )
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  //TODO: refactor code specially for reset
  searchCallback = (searchParams, button, reset: boolean) => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus,
      ...this.buildCreatedatetimeFilter(this.selectedViewForOption)
    }
    if (reset) {
      this.resetFilters()
    }
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.selectedViewForOption = $event.target.value
      const searchRequestBody = {
        ...this.searchRequestBody,
        ...this.buildCreatedatetimeFilter($event.target.value)
      }
      this.searchRequestBody = searchRequestBody
      this.queryParamValues.sortBy = 'createdDatetime'
      this.queryParamValues.sortOrder = 'DESC'
    } else {
      const searchRequestBody = { ...this.searchRequestBody, createdDatetimeRange: undefined }
      this.searchRequestBody = searchRequestBody
      this.selectedViewForOption = null
    }
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_ACTIVITY_SUMMARY_LIST + ServiceUrlConstants.API_EXPORT + documentType}`

    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  rowFormattingForDaysElapsed = (record: ActivityAPIModel): string => {
    if (record.daysElapsed > 7) {
      return 'priority-high'
    } else if (record.daysElapsed > 0 && record.daysElapsed <= 7) {
      return 'priority-medium'
    } else {
      return 'priority-low'
    }
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'mapReferenceDocuments',
        value: 'false'
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  dateSelection() {
    if (this.fromDate && this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: this.fromDate,
        toDate: this.toDate
      }
      this.loadSummaryData()
    }
    if (!this.fromDate && !this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: null,
        toDate: null
      }
      this.loadSummaryData()
    }
  }

  buildCreatedatetimeFilter(value: string) {
    if (!value) {
      return null
    }
    const daterange = value.split('^')
    return { createdDatetimeRange: { begin: new Date(daterange[0]), end: new Date(daterange[1]) } }
  }

  resetFilters() {
    this.isVisible = false
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = {
      ...this.searchRequestBody,
      ...this.summaryTableService.buildEmptyCreatedatetimeFilterIfValueIsNull(firstViewForOption)
    }
    this.queryParamValues = {
      pageNo: 0,
      pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
      mapReferenceDocuments: false,
      sortBy: 'createdDatetime',
      sortOrder: 'DESC'
    }
    this.fromDate = null
    this.toDate = null
    this.summaryData.contentSelector.selectedValue = this.selectedViewForOption
    this.cdr.detectChanges()
    this.isVisible = true
  }

  addFiltersWithDynamicOptions() {
    COMPANY_ENTITY_DISPLAY.filter.getOptionsList = () => {
      return this.companyEntityMap
    }
    COMPANY_ENTITY_DISPLAY.build = (data: any) => {
      return this.companyEntityMap?.get(data?.[COMPANY_ENTITY_DISPLAY.key])
    }
    this.summaryData?.headers?.splice(0, 0, COMPANY_ENTITY_DISPLAY)

    // Task Category
    TASK_SUB_CATEGORY.filter.getOptionsList = () => {
      return this.taskSubCategoryMap
    }
    this.summaryData?.headers?.splice(1, 0, TASK_SUB_CATEGORY)
  }

  ngOnDestroy(): void {
    this?.commonSubscription?.unsubscribe()
    this?.getSelectedCompaniesFromState$?.unsubscribe()
  }
}
